import { Flex, Text, InputGroup, InputLeftElement } from "@chakra-ui/react";
import { WithAuth } from "@/hoc";
import { useEffect, useState } from "react";
import { usePixLimit } from "@/hooks/usePixLimit";
import { FieldError, useForm } from "react-hook-form";
import { PixMiniIcon } from "@/styles/icons/PixMiniIcon";
import { formatCurrencyToApi ,showToast } from "@/utils";
import { InputCurrencyBRL } from "@/components";
import { MoonIcon } from "@/styles/icons/MoonIcon";
import { SunIcon } from "@/styles/icons/SunIcon";
import { CheckCircleIcon2 } from "@/styles/icons/CheckCircleIcon2";
import { CloseIcon } from "@/styles/icons/CloseIcon";
import { Button, Loading, Modal, PageHeading } from "barte-design-system";
import { useSettings } from "@/hooks";
import { LimitUpdateAlert } from "@/components/LimitUpdateAlert";
import { useAsyncFunction } from "@/hooks/useAsyncFunction";
import { useNavigate } from "react-router-dom";
import { Alert as AlertBarte } from "barte-design-system";

const DailyPixLimits = () => {
    const [isPixLimitDisable, setIsPixLimitDisable] = useState<boolean>(false);
    const [editionMode, setEditionMode] = useState<boolean>(false);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState<boolean>(false);
    const [initialDataSameOwnership, setInitialDataSameOwnership] = useState([]);
    const [initialDataOtherOwnership, setInitialDataOtherOwnership] = useState([]);
    const { getPixLimit, updatePixLimit } = usePixLimit();
    const { getBankingAccount } = useSettings();
    const navigate = useNavigate();

    const {
        setValue,
        getValues,
        watch,
        setError,
        clearErrors,
        reset,
        formState: { errors },
    } = useForm();

    const methods = [
        {
            title: "PIX",
            icon: <PixMiniIcon />,
            method: "pix",
        },
    ]
    const periods = ["DAY", "NIGHT"]
    function handleEditSection() {
        setEditionMode(!editionMode);
    }

    async function getInitialValues() {
        try {
            const response_banking = await getBankingAccount();
            const bankingData = response_banking?.data;

            if (!bankingData || bankingData.status === "INACTIVE") {
                setIsPixLimitDisable(true);
                return;
            }
            const response = await getPixLimit();
            const pixLimits = response?.data || [];
            const sameOwnershipLimits = pixLimits.filter((data: any) => data.isSameOwnership === true);
            const otherOwnershipLimits = pixLimits.filter((data: any) => data.isSameOwnership === false);

            sameOwnershipLimits.forEach((data: any) => {
                setValue(`${methods[0].method}Same${data.period}Amount`, data.scheduledAmount ?? data.amount);
                setValue(`${methods[0].method}Same${data.period}ScheduledAmount`, data.scheduledAmount);
            });
            otherOwnershipLimits.forEach((data: any) => {
                setValue(`${methods[0].method}Other${data.period}Amount`, data.scheduledAmount ?? data.amount);
                setValue(`${methods[0].method}Other${data.period}ScheduledAmount`, data.scheduledAmount);
            });

            setInitialDataSameOwnership(sameOwnershipLimits);
            setInitialDataOtherOwnership(otherOwnershipLimits);
        } catch (error) {
            setIsPixLimitDisable(true);
            return error;
        }
    }
    async function onSubmit() {
        const values = getValues();
        const sameOwnershipData = periods.map(period => ({
            period: period,
            amount: formatCurrencyToApi(values[`${methods[0].method}Same${period}Amount`]),
            isSameOwnership: true,
        }));
        const otherOwnershipData = periods.map(period => ({
            period: period,
            amount: formatCurrencyToApi(values[`${methods[0].method}Other${period}Amount`]),
            isSameOwnership: false,
        }));

        const sameOwnershipChanged = sameOwnershipData.filter(dados => {
            const found = initialDataSameOwnership.find((init: any) => init.period === dados.period);
            if (!found) return true;
            if(found.scheduledAmount) {
                return Number(found.scheduledAmount) !== Number(dados.amount);
            }
            return Number(found.amount) !== Number(dados.amount);
        });

        const otherOwnershipChanged = otherOwnershipData.filter(dados => {
            const found = initialDataOtherOwnership.find((init: any) => init.period === dados.period);
            if (!found) return true;
            if(found.scheduledAmount) {
                return Number(found.scheduledAmount) !== Number(dados.amount);
            }
            return Number(found.amount) !== Number(dados.amount);
        });

        const promises = [];

        try {
            for (const item of sameOwnershipChanged) {
                promises.push(updatePixLimit(item));
            }
            for (const item of otherOwnershipChanged) {
                promises.push(updatePixLimit(item));
            }

            await Promise.all(promises);

            if (promises.length > 0) {
                showToast({
                    type: "success",
                    message: "Limites alterado com sucesso.",
                });
                executeGetInitialValues();
            }
        } catch (error) {
            error?.response?.data?.errors.map((error) => {
                showToast({
                    type: "error",
                    message: `${error?.code} - ${error?.description}`
                })
            });
        } finally {
            setEditionMode(false);
            setIsSaveModalOpen(false);
        }
    }
    async function handleCancelEdit() {
        clearErrors();
        const resetValues: any = {};
        initialDataSameOwnership.forEach((data: any) => {
            const valueToUse = data.scheduledAmount ?? data.amount;
            resetValues[`${methods[0].method}Same${data.period}Amount`] = valueToUse;
            resetValues[`${methods[0].method}Same${data.period}ScheduledAmount`] = data.scheduledAmount;
        });

        initialDataOtherOwnership.forEach((data: any) => {
            const valueToUse = data.scheduledAmount ?? data.amount;
            resetValues[`${methods[0].method}Other${data.period}Amount`] = valueToUse;
            resetValues[`${methods[0].method}Other${data.period}ScheduledAmount`] = data.scheduledAmount;
        });
        reset(resetValues, { keepDefaultValues: false });
        setEditionMode(false);
    }

    const { loading: isLoadingInitialValues, execute: executeGetInitialValues } = useAsyncFunction(getInitialValues);
    const { loading: isLoadingUpdatePixLimit, execute: executeUpdatePixLimit } = useAsyncFunction(onSubmit);

    useEffect(() => {
        executeGetInitialValues();
    }, [])

    useEffect(() => {
        if (editionMode) {
            const getAmount = (field: string) => {
                const value = watch(field);
                return typeof value === "number" && !isNaN(value)
                    ? value
                    : formatCurrencyToApi(value);
            };
            const sameDayAmount = getAmount(`${methods[0].method}SameDAYAmount`);
            const sameNightAmount = getAmount(`${methods[0].method}SameNIGHTAmount`);
            const otherDayAmount = getAmount(`${methods[0].method}OtherDAYAmount`);
            const otherNightAmount = getAmount(`${methods[0].method}OtherNIGHTAmount`);
            
            if (otherDayAmount > sameDayAmount && sameDayAmount > 0) {
                setError(`${methods[0].method}OtherDAYAmount`, {
                    type: "manual",
                    message: "O limite para outras titularidades não pode ser maior que o de mesma titularidade."
                });
            } else {
                clearErrors(`${methods[0].method}OtherDAYAmount`);
            }
            if (otherNightAmount > sameNightAmount && sameNightAmount > 0) {
                setError(`${methods[0].method}OtherNIGHTAmount`, {
                    type: "manual",
                    message: "O limite para outras titularidades não pode ser maior que o de mesma titularidade."
                });
            } else {
                clearErrors(`${methods[0].method}OtherNIGHTAmount`);
            }
        }
    }, [
        watch(`${methods[0].method}SameDAYAmount`),
        watch(`${methods[0].method}SameNIGHTAmount`),
        watch(`${methods[0].method}OtherDAYAmount`),
        watch(`${methods[0].method}OtherNIGHTAmount`),
        editionMode
    ])

    const handleSaveClick = () => setIsSaveModalOpen(true);
    const handleIsSaveDisabled = () => {
        return watch("pixSameDAYAmount") === "" || 
        watch("pixSameNIGHTAmount") === "" ||
        watch("pixOtherDAYAmount") === "" || 
        watch("pixOtherNIGHTAmount") === "" ||
        errors?.[`${methods[0].method}OtherDAYAmount`]?.message !== undefined ||
        errors?.[`${methods[0].method}OtherNIGHTAmount`]?.message !== undefined
    }

    const hasScheduledAmount = () => {
        const allLimits = [...initialDataSameOwnership, ...initialDataOtherOwnership];
        return allLimits.some((limit: any) => limit.scheduledAmount && limit.scheduledAmount > 0);
    }

    const getScheduledAmount = (period: string, isSameOwnership: boolean) => {
        const limits = isSameOwnership ? initialDataSameOwnership : initialDataOtherOwnership;
        const limit = limits.find((l: any) => l.period === period);
        return limit?.amount || null;
    }
    return (
        <Flex
            w="100%"
            justify="center"
            flexDirection="column"
            gap="1rem"
        >
            <PageHeading
                title={"Limites diários"}
                primaryActionLabel={editionMode ? "Salvar" : "Editar"}
                onPrimaryAction={editionMode ? handleSaveClick : handleEditSection}
                secondaryActionLabel={editionMode ? "Cancelar" : null}
                onSecondaryAction={editionMode ? handleCancelEdit : null}
            />
            <Flex
                maxWidth="1280px"
                flexDir="column"
                width="100%"
                gap="1rem"
                py="4"
            >
                {!isLoadingInitialValues && !isPixLimitDisable && hasScheduledAmount() && (
                    <LimitUpdateAlert
                        sameOwnershipDayLimit={getScheduledAmount("DAY", true)}
                        sameOwnershipNightLimit={getScheduledAmount("NIGHT", true)}
                        otherOwnershipDayLimit={getScheduledAmount("DAY", false)}
                        otherOwnershipNightLimit={getScheduledAmount("NIGHT", false)}
                    />
                )}
                <Modal
                    isOpen={isSaveModalOpen}
                    title="Confirmar alteração de limites"
                    onClose={() => setIsSaveModalOpen(false)}
                    footer={{
                        primaryButton: {
                            children: "Confirmar",
                            onClick: executeUpdatePixLimit,
                            disabled: isLoadingUpdatePixLimit,
                            size: "sm",
                            round: "soft",
                        },
                        secondaryButton: {
                            children: "Cancelar",
                            onClick: () => setIsSaveModalOpen(false),
                            variant: "outline",
                            size: "sm",
                            round: "soft",
                        },
                    }}
                >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                        <CheckCircleIcon2 boxSize="24px" />
                        <Text fontSize="15px" fontWeight="400" color="#5e5c5d" lineHeight="1.2">
                            As mudanças de limite terão efeito após o período de 24 horas.
                        </Text>
                    </div>
                </Modal>
                {isLoadingInitialValues && (
                    <Flex
                        w="100%"
                        alignItems="center"
                        justify="center"
                    >
                        <Loading type="page" />
                    </Flex>
                )}
                {!isLoadingInitialValues && isPixLimitDisable && (
                    <AlertBarte
                        title="Conta não configurada."
                        variant="warning"
                        description="Para ter acesso ao ajuste de limites, é preciso realizar a configuração de sua conta."
                        showCloseButton={false}
                    />
                )}
                {!isLoadingInitialValues && !isPixLimitDisable && (
                    <Flex
                        w="100%"
                        flexDir="column"
                        gap="44px"
                    >
                        <Flex
                            w="100%"
                            flexDir="column"
                            gap="22px"
                        >
                            <Text
                                fontSize="16px"
                                fontWeight="600"
                                color="black"
                                letterSpacing="-0.16px"
                            >
                                Pix e boleto para mesma titularidade
                            </Text>
                            <Flex
                                w="100%"
                                maxW="360px"
                                flexDir="column"
                                gap="14px"
                            >
                                <Flex
                                    flexDir="column"
                                    gap="8px"
                                    w="100%"
                                >
                                    <Text
                                        fontSize="13px"
                                        fontWeight="500"
                                        color="#161616"
                                    >
                                        Dia
                                    </Text>
                                    <InputGroup  w="100%">
                                        <InputLeftElement
                                            pointerEvents="none"
                                            h="32px"
                                            pl="8px"
                                        >
                                            <SunIcon />
                                        </InputLeftElement>
                                        <InputCurrencyBRL
                                            value={watch(`${methods[0].method}SameDAYAmount`)}
                                            name={`${methods[0].method}SameDAYAmount`}
                                            onChangeInput={e => setValue(`${methods[0].method}SameDAYAmount`, e)}
                                            isLimit
                                            isDisabled={!editionMode}
                                            error={errors?.[`${methods[0].method}SameDAYAmount`] as FieldError}
                                        />
                                    </InputGroup>
                                </Flex>

                                <Flex
                                    flexDir="column"
                                    gap="8px"
                                    w="100%"
                                >
                                    <Text
                                        fontSize="13px"
                                        fontWeight="500"
                                        color="#161616"
                                    >
                                        Noite
                                    </Text>
                                    <InputGroup>
                                        <InputLeftElement
                                            pointerEvents="none"
                                            h="32px"
                                            pl="8px"
                                        >
                                            <MoonIcon />
                                        </InputLeftElement>
                                        <InputCurrencyBRL
                                            value={watch(`${methods[0].method}SameNIGHTAmount`)}
                                            name={`${methods[0].method}SameNIGHTAmount`}
                                            onChangeInput={e => setValue(`${methods[0].method}SameNIGHTAmount`, e)}
                                            fontSize="13px"
                                            h="32px"
                                            pl="38px"
                                            pr="8px"
                                            borderRadius="4px"
                                            border="1px solid #8d8d8d"
                                            bgColor="#ffffff"
                                            isLimit
                                            isDisabled={!editionMode}
                                            _focus={{
                                                borderColor: "#8d8d8d",
                                            }}
                                            error={errors?.[`${methods[0].method}SameNIGHTAmount`] as FieldError}
                                        />
                                    </InputGroup>
                                </Flex>
                            </Flex>
                        </Flex>
                        <Flex
                            w="100%"
                            flexDir="column"
                            gap="22px"
                        >
                            <Text
                                fontSize="16px"
                                fontWeight="600"
                                color="black"
                                letterSpacing="-0.16px"
                            >
                                Pix e boleto para outras titularidades
                            </Text>
                            <Flex
                                w="100%"
                                maxW="360px"
                                flexDir="column"
                                gap="14px"
                            >
                                <Flex
                                    flexDir="column"
                                    gap="8px"
                                    w="100%"
                                >
                                    <Text
                                        fontSize="13px"
                                        fontWeight="500"
                                        color="#161616"
                                    >
                                        Dia
                                    </Text>
                                    <InputGroup>
                                        <InputLeftElement
                                            pointerEvents="none"
                                            h="32px"
                                            pl="8px"
                                        >
                                            <SunIcon />
                                        </InputLeftElement>
                                        <InputCurrencyBRL
                                            value={watch(`${methods[0].method}OtherDAYAmount`)}
                                            name={`${methods[0].method}OtherDAYAmount`}
                                            onChangeInput={e => setValue(`${methods[0].method}OtherDAYAmount`, e)}
                                            fontSize="13px"
                                            h="32px"
                                            pl="38px"
                                            pr="8px"
                                            borderRadius="4px"
                                            border="1px solid #8d8d8d"
                                            bgColor="#ffffff"
                                            isLimit
                                            isDisabled={!editionMode}
                                            _focus={{
                                                borderColor: "#8d8d8d",
                                            }}
                                            error={errors?.[`${methods[0].method}OtherDAYAmount`] as FieldError}
                                        />
                                    </InputGroup>
                                </Flex>

                                <Flex
                                    flexDir="column"
                                    gap="8px"
                                    w="100%"
                                >
                                    <Text
                                        fontSize="13px"
                                        fontWeight="500"
                                        color="#161616"
                                    >
                                        Noite
                                    </Text>
                                    <InputGroup>
                                        <InputLeftElement
                                            pointerEvents="none"
                                            h="32px"
                                            pl="8px"
                                        >
                                            <MoonIcon />
                                        </InputLeftElement>
                                        <InputCurrencyBRL
                                            value={watch(`${methods[0].method}OtherNIGHTAmount`)}
                                            name={`${methods[0].method}OtherNIGHTAmount`}
                                            onChangeInput={e => setValue(`${methods[0].method}OtherNIGHTAmount`, e)}
                                            fontSize="13px"
                                            h="32px"
                                            pl="38px"
                                            pr="8px"
                                            borderRadius="4px"
                                            border="1px solid #8d8d8d"
                                            bgColor="#ffffff"
                                            isLimit
                                            isDisabled={!editionMode}
                                            _focus={{
                                                borderColor: "#8d8d8d",
                                            }}
                                            error={errors?.[`${methods[0].method}OtherNIGHTAmount`] as FieldError}
                                        />
                                    </InputGroup>
                                </Flex>
                            </Flex>
                        </Flex>
                    </Flex>
                )}
            </Flex>
        </Flex>
    );
};
export default WithAuth(DailyPixLimits);